import React, { useState, useEffect } from 'react';
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../../components/ui/table";
import Badge from "../../../components/ui/badge/Badge";
import Button from "../../../components/ui/button/Button";
import ComponentCard from "../../../components/common/ComponentCard";
import { Eye, FileText, Search, Filter, X, Calendar, DollarSign, Plus, Check, AlertCircle, CreditCard, FileEdit, Info, ClipboardList, Download, Edit2 } from 'lucide-react';
import Select from "../../../components/form/Select";
import Input from "../../../components/form/input/InputField";
import { Modal } from "../../../components/ui/modal";
import useApi from '../../../hooks/useApi';
import { toast } from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Nota } from '../types/index';
import CreateNotaModal from './CreateNotaModal';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import useAuth from "../../../hooks/useAuth";
import RoleBasedRenderer from "../../../components/auth/RoleBasedRenderer";

interface NotasListProps {
  notas: Nota[];
  onDataChange?: () => Promise<void>;
}

interface Filters {
  tipo: string;
  fechaInicio: string;
  fechaFin: string;
  facturaNumero: string;
  montoMin: string;
  montoMax: string;
  motivo: string;
  estado: string;
}

// Helper function to determine badge color based on tipo
const getTipoClasses = (tipo: string): string => {
  if (tipo === 'credito') return 'bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-500';
  if (tipo === 'debito') return 'bg-red-100 text-red-800 dark:bg-red-800/20 dark:text-red-500';
  return 'bg-gray-100 text-gray-800 dark:bg-gray-800/20 dark:text-gray-500';
};

// Helper function to determine badge color based on motivo
const getMotivoClasses = (motivo: string): string => {
  if (motivo === 'error') return 'bg-red-100 text-red-800 dark:bg-red-800/20 dark:text-red-500';
  if (motivo === 'descuento') return 'bg-blue-100 text-blue-800 dark:bg-blue-800/20 dark:text-blue-500';
  if (motivo === 'ajuste') return 'bg-amber-100 text-amber-800 dark:bg-amber-800/20 dark:text-amber-500';
  return 'bg-gray-100 text-gray-800 dark:bg-gray-800/20 dark:text-gray-500';
};

// Helper function to determine badge color based on estado
const getEstadoClasses = (estado: string): string => {
  if (estado === 'aplicada') return 'bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-500';
  if (estado === 'anulada') return 'bg-red-100 text-red-800 dark:bg-red-800/20 dark:text-red-500';
  return 'bg-gray-100 text-gray-800 dark:bg-gray-800/20 dark:text-gray-500';
};

const NotasList = ({ notas, onDataChange }: NotasListProps): JSX.Element => {
  const { formatCurrency, deleteNota } = useApi();
  const { canDelete, canCreate, canEdit } = useAuth();
  
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedNota, setSelectedNota] = useState<Nota | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [notasState, setNotas] = useState<Nota[]>([]);
  const [filters, setFilters] = useState<Filters>({
    tipo: '',
    fechaInicio: '',
    fechaFin: '',
    facturaNumero: '',
    montoMin: '',
    montoMax: '',
    motivo: '',
    estado: ''
  });

  useEffect(() => {
    setNotas(notas);
  }, [notas]);

  const handleViewDetails = (nota: Nota) => {
    setSelectedNota(nota);
    setIsDetailsModalOpen(true);
  };

  const handleEditNote = (nota: Nota) => {
    setSelectedNota(nota);
    setIsEditModalOpen(true);
  };

  const handleDeleteConfirm = (nota: Nota) => {
    setSelectedNota(nota);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteNota = async () => {
    if (!selectedNota?.id) return;
    
    try {
      setIsDeleting(true);
      await deleteNota(selectedNota.id);
      setIsDeleteModalOpen(false);
      toast.success(
        'Nota eliminada correctamente',
        {
          duration: 4000,
          icon: <Check className="w-4 h-4 text-green-500" />,
        }
      );
      if (onDataChange) {
        await onDataChange();
      }
    } catch (error) {
      console.error("Error deleting nota:", error);
      toast.error(
        'No se pudo eliminar la nota. Inténtalo de nuevo.',
        {
          duration: 5000,
          icon: <AlertCircle className="w-4 h-4 text-red-500" />,
        }
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExportNota = async (nota: Nota) => {
    if (!nota || !nota.id) {
      toast.error('Nota inválida');
      return;
    }
    
    try {
      // Create new PDF document
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Document dimensions
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      
      // Enhanced color palette
      const colors = {
        background: "#FFFFFF",
        primary: "#000000",
        text: "#1F2937",        // Slightly softer black for better readability
        textLight: "#6B7280",   // Gray-500
        border: "#E5E7EB",      // Gray-200 
        blue: "#2563EB",        // Blue-600
        blueLight: "#DBEAFE",   // Blue-100
        blueAccent: "#1E40AF",  // Darker blue for important elements
        success: "#059669",     // Deeper green for better visibility
        successLight: "#D1FAE5", // Green-100
        warning: "#D97706",     // Deeper amber for better visibility
        warningLight: "#FEF3C7", // Amber-100
        danger: "#DC2626",      // Deeper red for better visibility
        dangerLight: "#FEE2E2", // Red-100
        grayBg: "#F9FAFB",      // Gray-50
        grayMid: "#E5E7EB",     // Gray-200 for borders and dividers
      };

      // Function to add footer to the current page
      const addFooter = (pageNum: number) => {
        const footerY = pageHeight - 25;
        
        // Footer border
        doc.setDrawColor(colors.grayMid);
        doc.setLineWidth(0.7);
        doc.line(margin, footerY - 20, pageWidth - margin, footerY - 20);
        
        // Legal text
        doc.setFontSize(7.5);
        doc.setTextColor(colors.textLight);
        doc.setFont("helvetica", "normal");
        
        doc.text("* Este documento fue generado automáticamente.", margin, footerY - 14);
        doc.text("* Por favor, conserve este documento para su referencia.", margin, footerY - 10);
        
        // Page number
        doc.text(`Página ${pageNum}`, pageWidth - margin, footerY - 10, { align: "right" });
        
        // Contact information
        doc.setFontSize(8.5);
        doc.setTextColor(colors.textLight);
        
        const col1Width = contentWidth * 0.4;
        const col2Width = contentWidth * 0.3;
        
        // Contact information
        const contactEmail = 'contacto@destinycaribbeantours.com';
        const contactPhone = '+1 809 285-3179';
        
        // Contact info in footer
        doc.setFont("helvetica", "bold");
        doc.text("Destiny Caribbean Tours", margin, footerY - 3);
        doc.setFont("helvetica", "normal");
        doc.text(contactEmail, margin, footerY + 2);
        doc.text(contactPhone, margin, footerY + 7);
        
        // Website
        const websiteText = 'www.destinycaribbeantours.com';
        doc.setFont("helvetica", "normal");
        doc.text(websiteText, margin + col1Width + col2Width/2, footerY, { align: "center" });
        
        // Legal info
        const legalText = 'RNC: 131361765';
        doc.setFont("helvetica", "bold");
        doc.text(legalText, pageWidth - margin, footerY, { align: "right" });
      };
      
      // Function to check if we need a new page and handle page break
      const checkPageBreak = (currentY: number, neededHeight: number): number => {
        if (currentY + neededHeight > pageHeight - 40) {
          addFooter(doc.getNumberOfPages());
          doc.addPage();
          return margin + 15;
        }
        return currentY;
      };
      
      // -------- HEADER SECTION --------
      let currentY = 25;
      
      // Set the title based on the nota type
      const notaTitle = nota.tipo === 'credito' ? 'NOTA DE CRÉDITO' : 'NOTA DE DÉBITO';
      const notaColor = nota.tipo === 'credito' ? colors.success : colors.danger;
      
      // Title with type-based color
      doc.setFontSize(22);
      doc.setTextColor(notaColor);
      doc.setFont("helvetica", "bold");
      doc.text(notaTitle, margin, currentY + 5);
      
      // Nota number with accent color
      doc.setFontSize(16);
      doc.setTextColor(colors.blueAccent);
      doc.setFont("helvetica", "bold");
      doc.text(`#${nota.numero}`, margin, currentY + 15);
      
      // Right section - date and status
      doc.setFontSize(9);
      doc.setTextColor(colors.textLight);
      doc.setFont("helvetica", "normal");
      doc.text("Fecha:", pageWidth - margin - 37, currentY + 1);
      
      // Date value
      doc.setFontSize(10);
      doc.setTextColor(colors.text);
      doc.setFont("helvetica", "bold");
      const fechaStr = format(new Date(nota.fecha), 'dd/MM/yyyy');
      doc.text(fechaStr, pageWidth - margin, currentY + 1, { align: "right" });
      
      // Status label
      doc.setFontSize(9);
      doc.setTextColor(colors.textLight);
      doc.setFont("helvetica", "normal");
      doc.text("Estado:", pageWidth - margin - 37, currentY + 8);
      
      // Status value with color
      let statusText = nota.estado === 'aplicada' ? 'APLICADA' : 'ANULADA';
      let statusTextColor = nota.estado === 'aplicada' ? colors.success : colors.danger;
      
      doc.setFontSize(10);
      doc.setTextColor(statusTextColor);
      doc.setFont("helvetica", "bold");
      doc.text(statusText, pageWidth - margin, currentY + 8, { align: "right" });
      
      currentY += 20;
      
      // Border line below header
      doc.setDrawColor(colors.grayMid);
      doc.setLineWidth(0.7);
      doc.line(margin, currentY, pageWidth - margin, currentY);
      
      currentY += 15;
      
      // -------- INVOICE REFERENCE SECTION --------
      
      // Left column - Invoice reference with accent left border
      doc.setDrawColor(notaColor);
      doc.setLineWidth(1.7);
      doc.line(margin, currentY, margin, currentY + 23);
      
      doc.setFontSize(9);
      doc.setTextColor(colors.textLight);
      doc.setFont("helvetica", "bold");
      doc.text("FACTURA RELACIONADA", margin + 5, currentY);
      
      currentY += 7;
      
      // Client name
      doc.setFontSize(12);
      doc.setTextColor(colors.text);
      doc.setFont("helvetica", "bold");
      doc.text(nota.facturaNumero || 'Sin factura', margin + 5, currentY);
      
      currentY += 8;
      
      // Client info if available
      if (nota.clienteNombre) {
        doc.setFontSize(10);
        doc.setTextColor(colors.textLight);
        doc.setFont("helvetica", "normal");
        doc.text(nota.clienteNombre, margin + 5, currentY);
        currentY += 7;
      }
      
      // Right column - Nota details
      const detailsX = margin + contentWidth/2;
      currentY = currentY - 15; // Reset to start of section
      
      doc.setDrawColor("#9CA3AF");
      doc.setLineWidth(1.7);
      doc.line(detailsX, currentY, detailsX, currentY + 23);
      
      doc.setFontSize(9);
      doc.setTextColor(colors.textLight);
      doc.setFont("helvetica", "bold");
      doc.text("DETALLES", detailsX + 5, currentY);
      
      currentY += 7;
      
      // Add details rows
      const addDetailRow = (label: string, value: string, y: number) => {
        doc.setFontSize(9);
        doc.setTextColor(colors.textLight);
        doc.setFont("helvetica", "normal");
        doc.text(label, detailsX + 5, y);
        
        doc.setTextColor(colors.text);
        doc.setFont("helvetica", "bold");
        doc.text(value, detailsX + 38, y);
        
        return y + 7;
      };
      
      currentY = addDetailRow("Número:", nota.numero || '-', currentY);
      currentY = addDetailRow("Tipo:", nota.tipo === 'credito' ? 'Crédito' : 'Débito', currentY);
      currentY = addDetailRow("Motivo:", nota.motivo.charAt(0).toUpperCase() + nota.motivo.slice(1), currentY);
      
      currentY += 22;
      
      // -------- DESCRIPTION SECTION --------
      
      // Description header
      doc.setFontSize(10);
      doc.setTextColor(colors.blueAccent);
      doc.setFont("helvetica", "bold");
      doc.text("DESCRIPCIÓN", margin, currentY);
      
      doc.setDrawColor(colors.grayMid);
      doc.setLineWidth(0.7);
      doc.line(margin, currentY + 5, pageWidth - margin, currentY + 5);
      
      currentY += 12;
      
      // Description content with background
      doc.setFillColor(colors.grayBg);
      
      // Split the description into multiple lines if needed
      const splitDesc = doc.splitTextToSize(nota.descripcion || 'Sin descripción', contentWidth - 10);
      const descHeight = splitDesc.length * 7 + 8; // Text height + padding
      
      // Draw rounded rectangle for description background
      doc.roundedRect(margin, currentY - 4, contentWidth, descHeight, 2, 2, 'F');
      
      // Add description text
      doc.setFontSize(10);
      doc.setTextColor(colors.text);
      doc.setFont("helvetica", "normal");
      
      splitDesc.forEach((line: string, index: number) => {
        doc.text(line, margin + 5, currentY + (index * 7));
      });
      
      currentY += descHeight + 20;
      
      // -------- FINANCIAL DETAILS SECTION --------
      
      // Financial details header
      doc.setFontSize(10);
      doc.setTextColor(colors.blueAccent);
      doc.setFont("helvetica", "bold");
      doc.text("IMPACTO FINANCIERO", margin, currentY);
      
      doc.setDrawColor(colors.grayMid);
      doc.setLineWidth(0.7);
      doc.line(margin, currentY + 5, pageWidth - margin, currentY + 5);
      
      currentY += 15;
      
      // Financial impact table
      const financialY = currentY;
      
      // Right-aligned financial summary
      const summaryWidth = 85;
      const summaryX = pageWidth - margin - summaryWidth;
      
      // Add financial rows
      const addFinancialRow = (label: string, value: string, y: number, isBold: boolean = false, valueColor: any = null) => {
        doc.setFontSize(isBold ? 11 : 10);
        doc.setTextColor(colors.textLight);
        doc.setFont("helvetica", isBold ? "bold" : "normal");
        doc.text(label, summaryX, y);
        
        // Use custom color if provided
        if (valueColor) {
          doc.setTextColor(valueColor);
        } else {
          doc.setTextColor(isBold ? colors.blueAccent : colors.text);
        }
        
        doc.setFont("helvetica", isBold ? "bold" : "normal");
        doc.text(value, pageWidth - margin, y, { align: "right" });
        
        return y + 7;
      };
      
      // Monto
      currentY = addFinancialRow("Monto:", `$${nota.monto.toFixed(2)}`, currentY);
      
      // Impact on balance
      const impactSign = nota.tipo === 'credito' ? '-' : '+';
      const impactColor = nota.tipo === 'credito' ? colors.danger : colors.success;
      currentY = addFinancialRow(
        "Impacto en saldo:", 
        `${impactSign}$${Math.abs(nota.impactoSaldo).toFixed(2)}`, 
        currentY, 
        true, 
        impactColor
      );
      
      // Add some space at the bottom before footer
      currentY += 20;
      
      // Add footer to last page
      addFooter(doc.getNumberOfPages());
      
      // Save the PDF with a descriptive filename
      const pdfName = `Nota_${nota.tipo === 'credito' ? 'Credito' : 'Debito'}_${nota.numero}.pdf`;
      doc.save(pdfName);
      
      toast.success(
        'Nota exportada correctamente',
        {
          duration: 4000,
          icon: <Check className="w-4 h-4 text-green-500" />,
        }
      );
    } catch (error) {
      console.error("Error exporting nota:", error);
      toast.error(
        'No se pudo exportar la nota. Inténtalo de nuevo.',
        {
          duration: 5000,
          icon: <AlertCircle className="w-4 h-4 text-red-500" />,
        }
      );
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (value: string, name: string) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      tipo: '',
      fechaInicio: '',
      fechaFin: '',
      facturaNumero: '',
      montoMin: '',
      montoMax: '',
      motivo: '',
      estado: ''
    });
    setSearchTerm('');
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const formatMoneyValue = (value: number | undefined | null): string => {
    if (value === undefined || value === null) return formatCurrency(0);
    return formatCurrency(value);
  };

  // Apply filters and search to the original notas
  const filteredNotas = notasState.filter(nota => {
    // Text search (case insensitive)
    const searchLower = searchTerm.toLowerCase();
    if (searchTerm && !(
      (nota.numero && nota.numero.toLowerCase().includes(searchLower)) ||
      (nota.facturaNumero && nota.facturaNumero.toLowerCase().includes(searchLower)) ||
      (nota.descripcion && nota.descripcion.toLowerCase().includes(searchLower))
    )) {
      return false;
    }
    
    // Filter by tipo
    if (filters.tipo && nota.tipo !== filters.tipo) {
      return false;
    }
    
    // Filter by motivo
    if (filters.motivo && nota.motivo !== filters.motivo) {
      return false;
    }
    
    // Filter by estado
    if (filters.estado && nota.estado !== filters.estado) {
      return false;
    }
    
    // Filter by factura number
    if (filters.facturaNumero && !(nota.facturaNumero && nota.facturaNumero.includes(filters.facturaNumero))) {
      return false;
    }
    
    // Filter by date range - start date
    if (filters.fechaInicio && new Date(nota.fecha) < new Date(filters.fechaInicio)) {
      return false;
    }
    
    // Filter by date range - end date
    if (filters.fechaFin && new Date(nota.fecha) > new Date(filters.fechaFin)) {
      return false;
    }
    
    // Filter by min amount
    if (filters.montoMin && nota.monto < parseFloat(filters.montoMin)) {
      return false;
    }
    
    // Filter by max amount
    if (filters.montoMax && nota.monto > parseFloat(filters.montoMax)) {
      return false;
    }
    
    return true;
  });

  return (
    <ComponentCard title="Registro de notas" className="h-full">
      <div className="p-2 space-y-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Buscar por número, factura o descripción..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-2.5 text-sm text-gray-900 dark:text-white dark:border-gray-600 dark:bg-gray-800 dark:placeholder-gray-400 focus:ring-primary-500 focus:border-primary-500"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleFilters}
              startIcon={<Filter className="w-4 h-4" />}
            >
              Filtros
            </Button>
            <RoleBasedRenderer requiredPermission="create">
              <Button
                variant="primary"
                size="sm"
                startIcon={<Plus className="w-4 h-4" />}
                onClick={() => setIsCreateModalOpen(true)}
              >
                Nueva nota
              </Button>
            </RoleBasedRenderer>
          </div>
        </div>
      </div>

      {showFilters && (
        <div className="p-5 bg-gradient-to-br from-gray-50 to-white dark:from-gray-800/70 dark:to-gray-800/30 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 pb-3 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Filter className="w-4 h-4 text-primary-500" />
                Filtros avanzados
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Filtra las notas según los criterios que necesites
              </p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={clearFilters}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white"
            >
              <X className="w-4 h-4 mr-1.5" />
              Limpiar filtros
            </Button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-4">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <FileText className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                Tipo
              </label>
              <Select
                options={[
                  { value: '', label: 'Todos los tipos' },
                  { value: 'credito', label: 'Crédito' },
                  { value: 'debito', label: 'Débito' }
                ]}
                defaultValue={filters.tipo}
                onChange={(value) => handleFilterChange(value, 'tipo')}
                placeholder="Seleccionar tipo"
                className="w-full"
              />
            </div>
            
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <FileText className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                Motivo
              </label>
              <Select
                options={[
                  { value: '', label: 'Todos los motivos' },
                  { value: 'error', label: 'Error' },
                  { value: 'descuento', label: 'Descuento' },
                  { value: 'ajuste', label: 'Ajuste' },
                  { value: 'otro', label: 'Otro' }
                ]}
                defaultValue={filters.motivo}
                onChange={(value) => handleFilterChange(value, 'motivo')}
                placeholder="Seleccionar motivo"
                className="w-full"
              />
            </div>
            
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <FileText className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                Estado
              </label>
              <Select
                options={[
                  { value: '', label: 'Todos los estados' },
                  { value: 'aplicada', label: 'Aplicada' },
                  { value: 'anulada', label: 'Anulada' }
                ]}
                defaultValue={filters.estado}
                onChange={(value) => handleFilterChange(value, 'estado')}
                placeholder="Seleccionar estado"
                className="w-full"
              />
            </div>
            
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <FileText className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                Factura
              </label>
              <Input
                type="text"
                value={filters.facturaNumero}
                onChange={(e) => handleFilterChange(e.target.value, 'facturaNumero')}
                placeholder="Número de factura"
                className="w-full"
              />
            </div>
            
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Calendar className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                Desde
              </label>
              <Input
                type="date"
                value={filters.fechaInicio}
                onChange={(e) => handleFilterChange(e.target.value, 'fechaInicio')}
                onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.showPicker()}
                className="w-full"
              />
            </div>
            
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Calendar className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                Hasta
              </label>
              <Input
                type="date"
                value={filters.fechaFin}
                onChange={(e) => handleFilterChange(e.target.value, 'fechaFin')}
                onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.showPicker()}
                className="w-full"
              />
            </div>
            
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <DollarSign className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                Importe mínimo
              </label>
              <div className="relative">
                <Input
                  type="number"
                  value={filters.montoMin}
                  onChange={(e) => handleFilterChange(e.target.value, 'montoMin')}
                  placeholder="0.00"
                  className="w-full"
                />
              </div>
            </div>
            
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <DollarSign className="w-4 h-4 text-primary-500 inline-block mr-1.5" />
                Importe máximo
              </label>
              <div className="relative">
                <Input
                  type="number"
                  value={filters.montoMax}
                  onChange={(e) => handleFilterChange(e.target.value, 'montoMax')}
                  placeholder="0.00"
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>
      )}
<div className="scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent dark:scrollbar-track-gray-800/50 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <TableHeader className="bg-gray-50 dark:bg-gray-800/50">
            <TableRow>
              <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Número
              </TableCell>
              <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Fecha
              </TableCell>
              <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Factura
              </TableCell>
              <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Tipo
              </TableCell>
              <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Motivo
              </TableCell>
              <TableCell isHeader className="px-6 py-3.5 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Monto
              </TableCell>
              <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Estado
              </TableCell>
              <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Acciones
              </TableCell>
            </TableRow>
          </TableHeader>
          <TableBody className="bg-white dark:bg-transparent divide-y divide-gray-200 dark:divide-gray-700">
            {filteredNotas.length > 0 ? (
              filteredNotas.map((nota) => (
                <TableRow 
                  key={nota.id} 
                  className="hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors"
                >
                  <TableCell className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300 whitespace-nowrap font-medium">
                    {nota.numero}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300 whitespace-nowrap">
                    {format(new Date(nota.fecha), 'dd/MM/yyyy')}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300 whitespace-nowrap">
                    {nota.facturaNumero || (
                      <span className="text-gray-500 italic">
                        Sin factura
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getTipoClasses(nota.tipo)}`}>
                      {nota.tipo === 'credito' ? 'Crédito' : 'Débito'}
                    </span>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getMotivoClasses(nota.motivo)}`}>
                      {nota.motivo === 'error' ? 'Error' : 
                       nota.motivo === 'descuento' ? 'Descuento' : 
                       nota.motivo === 'ajuste' ? 'Ajuste' : 'Otro'}
                    </span>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300 text-right whitespace-nowrap font-medium">
                    {formatMoneyValue(nota.monto)}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getEstadoClasses(nota.estado)}`}>
                      {nota.estado === 'aplicada' ? 'Aplicada' : 'Anulada'}
                    </span>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm">
                    <div className="flex space-x-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleViewDetails(nota)}
                        aria-label="Ver detalles"
                        className="text-gray-500 hover:text-gray-700 hover:border-gray-500"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <RoleBasedRenderer requiredPermission="edit">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleEditNote(nota)}
                          aria-label="Editar nota"
                          className="text-blue-500 hover:text-blue-700 hover:border-blue-500"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      </RoleBasedRenderer>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleExportNota(nota)}
                        aria-label="Exportar PDF"
                        className="text-blue-500 hover:text-blue-700 hover:border-blue-500"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <RoleBasedRenderer requiredPermission="delete">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleDeleteConfirm(nota)}
                          aria-label="Eliminar nota"
                          className="text-red-500 hover:text-red-700 hover:border-red-500"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </RoleBasedRenderer>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  {searchTerm || Object.values(filters).some(f => f !== '') ? (
                    <div>
                      <p>No se encontraron notas con los filtros aplicados.</p>
                    </div>
                  ) : (
                    <p>No hay notas registradas en el sistema.</p>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Modal para ver detalles */}
      {selectedNota && (
        <Modal
          isOpen={isDetailsModalOpen}
          onClose={() => setIsDetailsModalOpen(false)}
          title={`Nota ${selectedNota.tipo === 'credito' ? 'de Crédito' : 'de Débito'} #${selectedNota.numero}`}
        >
          <div className="p-5 max-h-[80vh] overflow-y-auto">
            {/* Header with amount and date */}
            <div className="mb-6 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-5 border border-blue-200 dark:border-blue-800">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {formatMoneyValue(selectedNota.monto)}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {format(new Date(selectedNota.fecha), 'dd MMMM yyyy', { locale: es })}
                  </p>
                </div>
                <div className="flex space-x-2">
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${getTipoClasses(selectedNota.tipo)}`}>
                    {selectedNota.tipo === 'credito' ? 'Crédito' : 'Débito'}
                  </span>
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${getEstadoClasses(selectedNota.estado)}`}>
                    {selectedNota.estado === 'aplicada' ? 'Aplicada' : 'Anulada'}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
              {/* Factura information */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="bg-gray-50 dark:bg-gray-800/50 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-white flex items-center">
                    <FileText className="w-4 h-4 mr-2 text-primary-500" />
                    Información de Factura
                  </h3>
                </div>
                <div className="p-4">
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Número de Factura</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {selectedNota.facturaNumero || "Sin factura asociada"}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Impacto en Saldo</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatMoneyValue(selectedNota.impactoSaldo)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Motivo information */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="bg-gray-50 dark:bg-gray-800/50 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-white flex items-center">
                    <Info className="w-4 h-4 mr-2 text-primary-500" />
                    Detalles del Motivo
                  </h3>
                </div>
                <div className="p-4">
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Motivo</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      <span className={getMotivoClasses(selectedNota.motivo)}>
                        {selectedNota.motivo === 'error' ? 'Error' : 
                        selectedNota.motivo === 'descuento' ? 'Descuento' : 
                        selectedNota.motivo === 'ajuste' ? 'Ajuste' : 'Otro'}
                      </span>
                    </p>
                  </div>
                  {selectedNota.motivoDetalle && (
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Detalle del motivo</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {selectedNota.motivoDetalle}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Descripción */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm mb-6">
              <div className="bg-gray-50 dark:bg-gray-800/50 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-medium text-gray-700 dark:text-white flex items-center">
                  <ClipboardList className="w-4 h-4 mr-2 text-primary-500" />
                  Descripción
                </h3>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {selectedNota.descripcion || "Sin descripción"}
                </p>
              </div>
            </div>

            {/* Metadata section with ID and timestamps */}
            <div className="bg-gray-50 dark:bg-gray-800/30 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="grid grid-cols-2 gap-4 text-xs text-gray-500 dark:text-gray-400">
                <div>
                  <p>ID de la Nota</p>
                  <p className="font-medium text-gray-700 dark:text-gray-300">{selectedNota.id}</p>
                </div>
                {selectedNota.createdAt && (
                  <div>
                    <p>Fecha de registro</p>
                    <p className="font-medium text-gray-700 dark:text-gray-300">
                      {format(new Date(selectedNota.createdAt), 'dd/MM/yyyy HH:mm', { locale: es })}
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setIsDetailsModalOpen(false)}
              >
                Cerrar
              </Button>
              <Button
                variant="outline"
                onClick={() => handleExportNota(selectedNota)}
                className="bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100 hover:border-blue-300 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800 dark:hover:bg-blue-900/30"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar PDF
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setIsDetailsModalOpen(false);
                  handleEditNote(selectedNota);
                }}
              >
                Editar Nota
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal para eliminar nota */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedNota(null);
        }}
        title="Confirmación de eliminación"
        size="md"
      >
        <div className="space-y-6 px-2">
          <div className="space-y-4">
            <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
              ¿Está seguro de eliminar esta nota?
            </p>
            
            <div className="space-y-3">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                Esta acción eliminará permanentemente todos los datos asociados a la nota, incluyendo:
              </p>
              
              <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 ml-4 space-y-1">
                <li>Información de la nota</li>
                <li>El impacto en saldo asociado</li>
                <li>Historial financiero relacionado</li>
                <li>Cualquier otro dato asociado</li>
              </ul>
            </div>

            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-lg">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                Esta acción no se puede deshacer.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setSelectedNota(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleDeleteNota}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeleting ? 'Eliminando...' : 'Sí, eliminar nota'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal para editar nota */}
      {selectedNota && (
        <CreateNotaModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={onDataChange ? onDataChange : () => Promise.resolve()}
          notaToEdit={selectedNota}
        />
      )}
      
      {/* Modal para crear nueva nota */}
      <CreateNotaModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={onDataChange ? onDataChange : () => Promise.resolve()}
      />
    </ComponentCard>
  );
};

export default NotasList; 